import { ensurePartWidthsSum } from './partWidthDistribution';

export const MAX_PART_BAND_SECTIONS = 2;

export const clampPartBandSectionCount = (count) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(MAX_PART_BAND_SECTIONS, Math.round(n));
};

export const resizePerPartBandSectionConfig = (prev, partsCount) => {
  const resizeCounts = (arr) =>
    Array.from({ length: partsCount }, (_, i) => clampPartBandSectionCount(arr?.[i] ?? 1));

  const resizeNested = (arr, fallback) =>
    Array.from({ length: partsCount }, (_, i) => {
      if (Array.isArray(arr?.[i]) && arr[i].length > 0) return [...arr[i]];
      return Array.isArray(fallback) ? [...fallback] : fallback;
    });

  return {
    partOverheadSections: resizeCounts(prev.partOverheadSections),
    partUnderlightSections: resizeCounts(prev.partUnderlightSections),
    partOverheadSectionWindowTypes: resizeNested(prev.partOverheadSectionWindowTypes, ['fixed-sash']),
    partUnderlightSectionWindowTypes: resizeNested(prev.partUnderlightSectionWindowTypes, ['fixed-sash']),
    partOverheadSectionWidths: resizeNested(prev.partOverheadSectionWidths, null),
    partUnderlightSectionWidths: resizeNested(prev.partUnderlightSectionWidths, null),
  };
};

export const getPartBandSectionConfig = ({
  partIndex,
  partWidthMm,
  legacyWindowTypes,
  sections,
  sectionWindowTypes,
  sectionWidths,
}) => {
  const count = clampPartBandSectionCount(sections?.[partIndex] ?? 1);
  const legacyType = legacyWindowTypes?.[partIndex] ?? 'fixed-sash';
  const typesRow = sectionWindowTypes?.[partIndex];
  const windowTypes = Array.from({ length: count }, (_, i) => typesRow?.[i] ?? legacyType);
  const widthsMm = ensurePartWidthsSum(sectionWidths?.[partIndex], count, partWidthMm);
  return { count, windowTypes, widthsMm };
};

export const buildPartBandSectionLayouts = (innerWidthPx, innerWidthMm, count, widthsMm, windowTypes) => {
  const sectionCount = clampPartBandSectionCount(count);
  const normalizedWidthsMm = ensurePartWidthsSum(widthsMm, sectionCount, innerWidthMm);
  const totalMm = normalizedWidthsMm.reduce((sum, w) => sum + w, 0) || 1;
  let accumMm = 0;

  return Array.from({ length: sectionCount }, (_, sectionIndex) => {
    const widthMm = normalizedWidthsMm[sectionIndex];
    const xOffset = (accumMm / totalMm) * innerWidthPx;
    const widthPx = (widthMm / totalMm) * innerWidthPx;
    accumMm += widthMm;
    return {
      sectionIndex,
      xOffset,
      widthPx,
      widthMm,
      windowType: windowTypes?.[sectionIndex] ?? 'fixed-sash',
    };
  });
};

export const applyPartBandSectionCount = (prev, band, partIndex, newCount, partWidthMm) => {
  const count = clampPartBandSectionCount(newCount);
  const sectionsField = band === 'overhead' ? 'partOverheadSections' : 'partUnderlightSections';
  const typesField = band === 'overhead' ? 'partOverheadSectionWindowTypes' : 'partUnderlightSectionWindowTypes';
  const widthsField = band === 'overhead' ? 'partOverheadSectionWidths' : 'partUnderlightSectionWidths';
  const legacyField = band === 'overhead' ? 'partOverheadWindowTypes' : 'partUnderlightWindowTypes';
  const partsCount = prev.parts || 1;

  const sections = Array.from({ length: partsCount }, (_, i) =>
    i === partIndex ? count : clampPartBandSectionCount(prev[sectionsField]?.[i] ?? 1)
  );

  const legacyType = prev[legacyField]?.[partIndex] ?? 'fixed-sash';
  const existingTypes = prev[typesField]?.[partIndex];
  const windowTypes = Array.from({ length: count }, (_, i) => existingTypes?.[i] ?? legacyType);

  const types = Array.from({ length: partsCount }, (_, i) => {
    if (i === partIndex) return windowTypes;
    const row = prev[typesField]?.[i];
    if (Array.isArray(row) && row.length > 0) return [...row];
    return [prev[legacyField]?.[i] ?? 'fixed-sash'];
  });

  const widths = Array.from({ length: partsCount }, (_, i) => {
    if (i === partIndex) return ensurePartWidthsSum(prev[widthsField]?.[i], count, partWidthMm);
    return prev[widthsField]?.[i] ?? null;
  });

  const legacy = [...(prev[legacyField] || Array(partsCount).fill('fixed-sash'))];
  legacy[partIndex] = windowTypes[0];

  return {
    ...prev,
    [sectionsField]: sections,
    [typesField]: types,
    [widthsField]: widths,
    [legacyField]: legacy,
  };
};

export const applyPartBandSectionWindowType = (prev, band, partIndex, sectionIndex, windowTypeId) => {
  const typesField = band === 'overhead' ? 'partOverheadSectionWindowTypes' : 'partUnderlightSectionWindowTypes';
  const sectionsField = band === 'overhead' ? 'partOverheadSections' : 'partUnderlightSections';
  const legacyField = band === 'overhead' ? 'partOverheadWindowTypes' : 'partUnderlightWindowTypes';
  const partsCount = prev.parts || 1;
  const sectionCount = clampPartBandSectionCount(prev[sectionsField]?.[partIndex] ?? 1);

  const types = Array.from({ length: partsCount }, (_, i) => {
    const row = Array.from({ length: sectionCount }, (_, si) => {
      const source = prev[typesField]?.[i];
      if (i === partIndex && si === sectionIndex) return windowTypeId;
      return source?.[si] ?? prev[legacyField]?.[i] ?? 'fixed-sash';
    });
    if (i === partIndex) return row;
    const existing = prev[typesField]?.[i];
    if (Array.isArray(existing) && existing.length > 0) return [...existing];
    return [prev[legacyField]?.[i] ?? 'fixed-sash'];
  });

  const legacy = [...(prev[legacyField] || Array(partsCount).fill('fixed-sash'))];
  if (sectionIndex === 0) legacy[partIndex] = windowTypeId;

  return { ...prev, [typesField]: types, [legacyField]: legacy };
};

export const applyPartBandSectionWidth = (prev, band, partIndex, sectionIndex, newWidthMm, partWidthMm) => {
  const widthsField = band === 'overhead' ? 'partOverheadSectionWidths' : 'partUnderlightSectionWidths';
  const sectionsField = band === 'overhead' ? 'partOverheadSections' : 'partUnderlightSections';
  const partsCount = prev.parts || 1;
  const sectionCount = clampPartBandSectionCount(prev[sectionsField]?.[partIndex] ?? 1);

  const current = ensurePartWidthsSum(prev[widthsField]?.[partIndex], sectionCount, partWidthMm);
  const next = [...current];
  next[sectionIndex] = newWidthMm;
  const normalized = ensurePartWidthsSum(next, sectionCount, partWidthMm);

  const widths = Array.from({ length: partsCount }, (_, i) => {
    if (i === partIndex) return normalized;
    return prev[widthsField]?.[i] ?? null;
  });

  return { ...prev, [widthsField]: widths };
};
